import { computed, type Ref } from 'vue';
import type { CrmActionGroup, CrmActivity } from '@/types/crm';

type SegmentPolicyKey = CrmActivity['crm_segment_type'];
type ActivityState = 'pause-edit' | 'edit' | 'rework-edit' | 'rework' | 'check';

type EditProfile = 'full' | 'actions-only' | false;
type ReviewProfile = 'view' | 'moderate' | false;

type PolicyProfile = {
  edit: EditProfile;
  review: ReviewProfile;
  permissions?: {
    canDeleteActionGroup?: boolean;
    canToggleControlGroup?: boolean;
  };
};

export type ActivityActionsModes = {
  isStructureLocked: boolean;
  isFullEditMode: boolean;
  isHasControlGroup: boolean;
  isActionsEditState: boolean;
  isReviewState: boolean;
  isActionsCheckControlVisible: boolean;
  isCheckControlEditable: boolean;
  isControlGroupPanelVisible: boolean;
};

export type ActivityActionsPermissions = {
  canToggleControlGroup: boolean;
  canDeleteActionGroup: (group: CrmActionGroup) => boolean;
  canDeleteAction: (group: CrmActionGroup, type: string) => boolean;
  canEditAction: (group: CrmActionGroup, type: string) => boolean;
};

const ACTIONS_ONLY_TYPES = ['email', 'sms', 'call'];

const SEGMENT_POLICY_KEYS: SegmentPolicyKey[] = ['dynamic', 'fixed', 'manual'];

const EMPTY_PROFILE: PolicyProfile = { edit: false, review: false };

const FIXED_POLICY: Record<ActivityState, PolicyProfile> = {
  'pause-edit': {
    edit: 'actions-only',
    review: false,
    permissions: { canDeleteActionGroup: false, canToggleControlGroup: false },
  },
  edit: { edit: 'full', review: false },
  'rework-edit': { edit: 'full', review: false },
  rework: { edit: false, review: 'view' },
  check: { edit: false, review: 'moderate' },
};

const POLICY: Record<SegmentPolicyKey, Record<ActivityState, PolicyProfile>> = {
  dynamic: {
    ...FIXED_POLICY,
    'pause-edit': { edit: 'full', review: false },
  },
  fixed: FIXED_POLICY,
  manual: FIXED_POLICY,
};

interface UseActivityActionsPolicyOptions {
  activity: Ref<CrmActivity>;
  state: Ref<string>;
  controlGroupActive: Ref<boolean>;
  canReview: Ref<boolean>;
}

export const useActivityActionsPolicy = ({
  activity,
  state,
  controlGroupActive,
  canReview,
}: UseActivityActionsPolicyOptions) => {
  const profile = computed(() => {
    const segmentType = SEGMENT_POLICY_KEYS.includes(activity.value.crm_segment_type)
      ? activity.value.crm_segment_type
      : 'fixed';

    return POLICY[segmentType][state.value as ActivityState] ?? EMPTY_PROFILE;
  });

  const modes = computed((): ActivityActionsModes => {
    const currentProfile = profile.value;
    const hasControlGroup = (activity.value.action_groups || []).some((group) => group.is_control);
    const isFullEditMode = currentProfile.edit === 'full';
    const isActionsEditState = currentProfile.edit !== false;

    return {
      isStructureLocked: currentProfile.edit === 'actions-only',
      isFullEditMode,
      isHasControlGroup: hasControlGroup,
      isActionsEditState,
      isReviewState: currentProfile.review !== false,
      isActionsCheckControlVisible: currentProfile.review === 'view'
        || (currentProfile.review === 'moderate' && canReview.value),
      isCheckControlEditable: currentProfile.review === 'moderate',
      isControlGroupPanelVisible: (isActionsEditState || hasControlGroup)
        && (controlGroupActive.value || isFullEditMode),
    };
  });

  const permissions = computed((): ActivityActionsPermissions => {
    const currentProfile = profile.value;
    const policyPermissions = currentProfile.permissions ?? {};
    const isFullEdit = currentProfile.edit === 'full';
    const isActionsOnly = currentProfile.edit === 'actions-only';

    return {
      canToggleControlGroup: policyPermissions.canToggleControlGroup ?? true,
      canDeleteActionGroup: () => policyPermissions.canDeleteActionGroup ?? true,
      canDeleteAction: () => true,
      canEditAction: (_group, type) => isFullEdit
        || (isActionsOnly && ACTIONS_ONLY_TYPES.includes(type)),
    };
  });

  return { modes, permissions };
};
